// ========================================
// PII REVEAL CONTEXT
// frontend/src/contexts/PiiRevealContext.js
//
// For tenants with PII masking on (tenant_org.products.mask_pii), the backend
// sends phone/email pre-masked, so display is masked everywhere automatically.
// This context provides the ONE sanctioned way to see the raw value: a confirm
// dialog ("Reveal PII? This action will be recorded") that, on confirm, hits
// POST /api/pii/reveal — which writes a PII_REVEAL audit row and returns the raw
// contact for that single record. Reveals are per-record and ephemeral (held in
// component state; they reset on navigation/reload).
// ========================================

import { createContext, useContext, useState, useCallback } from 'react';
import { useTenant } from './TenantContext';
import api from '../services/api';

const PiiRevealContext = createContext({ maskPii: false, requestReveal: async () => null });

export const usePiiReveal = () => useContext(PiiRevealContext);

export function PiiRevealProvider({ children }) {
  const { tenant } = useTenant();
  const maskPii = tenant?.products?.mask_pii === true;

  // pending = { entityType, entityId, resolve } while the confirm dialog is open
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Returns a Promise resolving to { phone, email, whatsapp_number } on confirm,
  // or null if the user cancels / the reveal fails.
  const requestReveal = useCallback((entityType, entityId) => {
    if (!entityId) return Promise.resolve(null);
    return new Promise((resolve) => {
      setError(null);
      setPending({ entityType, entityId, resolve });
    });
  }, []);

  const handleConfirm = async () => {
    if (!pending || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/pii/reveal', {
        entityType: pending.entityType,
        entityId: pending.entityId,
      });
      pending.resolve(res.data || null);
      setPending(null);
    } catch (e) {
      setError('Could not reveal. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    if (busy) return;
    if (pending) pending.resolve(null);
    setPending(null);
    setError(null);
  };

  return (
    <PiiRevealContext.Provider value={{ maskPii, requestReveal }}>
      {children}
      {pending && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
          onClick={handleCancel}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900">Reveal contact details?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This action will be recorded. Your name and the time of this reveal are
              logged for this record.
            </p>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleCancel}
                disabled={busy}
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50"
              >
                {busy ? 'Revealing…' : 'Reveal & record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PiiRevealContext.Provider>
  );
}

// Per-record reveal hook. `revealed` is null until the user confirms a reveal
// for this record, then holds { phone, email, whatsapp_number }. Ephemeral —
// lives in component state, so it re-masks when the component unmounts.
export function useRecordReveal(entityType, entityId) {
  const { maskPii, requestReveal } = usePiiReveal();
  const [revealed, setRevealed] = useState(null);

  const reveal = useCallback(async () => {
    const data = await requestReveal(entityType, entityId);
    if (data) setRevealed(data);
    return data;
  }, [entityType, entityId, requestReveal]);

  return { maskPii, revealed, reveal };
}
